import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { useMapStore } from '../../stores/mapStore.js';
import { LoadingSpinner } from '../ui/index.js';
import type { DayScene, Scene, Pin } from '../../types/index.js';

interface DaySceneListProps {
  dayId: string;
}

export function DaySceneList({ dayId }: DaySceneListProps) {
  const qc = useQueryClient();
  const { flyTo, activeProject } = useMapStore();
  const sensors = useSensors(useSensor(PointerSensor));
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['day-scenes', dayId],
    queryFn: async () => {
      const result = await api.get<DayScene[]>(`/days/${dayId}/scenes`);
      return result.success ? result.data : [];
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (scenes: DayScene[]) =>
      api.put(`/days/${dayId}/scenes`, {
        scenes: scenes.map(s => ({ sceneId: s.sceneId, departureTime: s.departureTime, travelNotes: s.travelNotes })),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['day-scenes', dayId] }),
    onError: () => toast.error('Failed to reorder scenes'),
  });

  const removeMutation = useMutation({
    mutationFn: (sceneId: string) => api.delete(`/days/${dayId}/scenes?sceneId=${sceneId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['day-scenes', dayId] }),
  });

  const addSceneMutation = useMutation({
    mutationFn: (sceneId: string) => api.post(`/days/${dayId}/scenes`, { sceneId }),
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['day-scenes', dayId] });
      toast.success('Scene added');
      setPickerOpen(false);
    },
  });

  const { data: allScenes } = useQuery({
    queryKey: ['scenes', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];
      const result = await api.get<{ scenes: Scene[] }>(`/scenes?projectId=${activeProject.id}&limit=200`);
      return result.success ? result.data.scenes : [];
    },
    enabled: !!activeProject && pickerOpen,
  });

  const scenes = data ?? [];
  const addedSceneIds = new Set(scenes.map(s => s.sceneId));

  const handleDragEnd = (event: { active: { id: unknown }; over: { id: unknown } | null }) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = scenes.findIndex(s => s.id === active.id);
    const newIdx = scenes.findIndex(s => s.id === over.id);
    const reordered = arrayMove(scenes, oldIdx, newIdx);
    reorderMutation.mutate(reordered);
  };

  if (isLoading) return <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>;

  const availableScenes = (allScenes ?? []).filter(s => !addedSceneIds.has(s.id));

  return (
    <div className="mt-3 space-y-2">
      {scenes.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-2">No scenes yet</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={scenes.map(s => s.id)} strategy={verticalListSortingStrategy}>
            {scenes.map((ds, i) => (
              <SortableSceneItem
                key={ds.id}
                item={ds}
                index={i}
                onFly={() => ds.lat && ds.lng && flyTo(ds.lng as number, ds.lat as number)}
                onRemove={() => removeMutation.mutate(ds.sceneId)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Add scene picker */}
      <div className="relative">
        <button
          onClick={() => setPickerOpen(o => !o)}
          className="w-full text-xs text-brand-600 hover:text-brand-700 font-medium py-1.5 border border-dashed border-brand-300 hover:border-brand-400 rounded-lg transition-colors"
        >
          + Add Scene
        </button>
        {pickerOpen && (
          <div className="absolute bottom-full mb-1 left-0 right-0 z-50 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
            {availableScenes.length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-2 text-center">
                {(allScenes ?? []).length === 0 ? 'No scenes in this project' : 'All scenes already added'}
              </p>
            ) : (
              <ul className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                {availableScenes.map(s => (
                  <li
                    key={s.id}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 cursor-pointer"
                    onClick={() => addSceneMutation.mutate(s.id)}
                  >
                    {s.name}
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-gray-100 px-3 py-1.5">
              <button onClick={() => setPickerOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SortableSceneItem({
  item, index, onFly, onRemove,
}: {
  item: DayScene; index: number; onFly: () => void; onRemove: () => void;
}) {
  const qc = useQueryClient();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  // Pins for this scene (only fetched when picker is open)
  const { data: pinsData } = useQuery({
    queryKey: ['pins', item.sceneId],
    queryFn: async () => {
      const result = await api.get<{ pins: Pin[] }>(`/pins?sceneId=${item.sceneId}&limit=200`);
      return result.success ? result.data : { pins: [] };
    },
    enabled: locationPickerOpen,
  });

  const setActivePinMutation = useMutation({
    mutationFn: ({ sceneId, pinId }: { sceneId: string; pinId: string | null }) =>
      api.put(`/scenes/${sceneId}`, { activePinId: pinId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scenes'] });
      qc.invalidateQueries({ queryKey: ['day-scenes', item.dayId] });
      setLocationPickerOpen(false);
      toast.success('Active location updated');
    },
  });

  const mainPins = (pinsData?.pins ?? []).filter(p => p.pinType === 'main');

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 relative">
      <span className="text-gray-400 cursor-grab" {...attributes} {...listeners}>⠿</span>
      <span className="text-xs text-gray-500 w-4">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{item.sceneName ?? 'Scene'}</p>
        {item.departureTime && <p className="text-xs text-gray-400">🕐 {item.departureTime}</p>}
      </div>
      <button onClick={onFly} className="text-gray-400 hover:text-brand-500 text-xs" title="Fly to">🎯</button>
      {/* Switch active location */}
      <button
        onClick={() => setLocationPickerOpen(o => !o)}
        className="text-gray-400 hover:text-brand-500 text-xs"
        title="Switch active location"
      >⭐</button>
      {locationPickerOpen && (
        <div className="absolute right-8 top-8 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-48 overflow-hidden">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 py-2 border-b border-gray-100">Active Location</p>
          {!pinsData ? (
            <div className="flex justify-center py-3"><LoadingSpinner size="sm" /></div>
          ) : mainPins.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2">No locations in this scene</p>
          ) : (
            <ul className="max-h-40 overflow-y-auto divide-y divide-gray-50">
              {mainPins.map(pin => (
                <li
                  key={pin.id}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 cursor-pointer"
                  onClick={() => setActivePinMutation.mutate({ sceneId: item.sceneId, pinId: pin.id })}
                >
                  {pin.title}
                  <p className="text-xs text-gray-400 truncate">{pin.address}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-gray-100 px-3 py-1.5">
            <button onClick={() => setLocationPickerOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
        </div>
      )}
      <button onClick={onRemove} className="text-gray-400 hover:text-red-500 text-xs" title="Remove">✕</button>
    </div>
  );
}
